/**
 * GGJ brand logo — Same lockup as gograbjob.com: ggj-mark-ref.png + mix-blend-mode: screen
 * on brand purple. Full mark (circle + GGJ) is fitted with object-fit: contain so it matches
 * marketing / login. Light theme adds a subtle border + shadow so the tile reads on white chrome.
 */
import { useTheme } from '@contexts/ThemeContext';

interface GGJLogoFullProps {
  iconSize?: number;
  fontSize?: number;
  textColor?: string;
  className?: string;
}

export function GGJLogoFull({
  iconSize = 34,
  fontSize = 15,
  textColor,
  className = '',
}: GGJLogoFullProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const color = textColor ?? 'var(--vscode-text-primary)';

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <div
        className={`shrink-0 overflow-hidden rounded-xl ${
          isLight
            ? 'border border-violet-400/70 shadow-[0_3px_18px_rgba(91,79,229,0.32)]'
            : 'ring-1 ring-white/10'
        }`}
        style={{
          width: iconSize,
          height: iconSize,
          background: 'var(--ggj-logo-mark-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: Math.max(2, Math.round(iconSize * 0.06)),
        }}
      >
        <img
          src="/ggj-mark-ref.png"
          alt=""
          draggable={false}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            mixBlendMode: 'screen',
            flexShrink: 0,
          }}
        />
      </div>
      <span
        className="font-bold whitespace-nowrap tracking-tight"
        style={{ fontSize, color, lineHeight: 1 }}
      >
        GoGrabJob
      </span>
    </div>
  );
}

interface GGJLogoMarkProps {
  size?: number;
  className?: string;
  /** Use dark-surface chrome (ring, no light border) even when app theme is light — e.g. footer on black. */
  forceDarkSurface?: boolean;
}

export function GGJLogoMark({ size = 40, className = '', forceDarkSurface = false }: GGJLogoMarkProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light' && !forceDarkSurface;

  return (
    <div
      className={`shrink-0 overflow-hidden rounded-xl ${
        isLight
          ? 'border border-violet-400/70 shadow-[0_3px_18px_rgba(91,79,229,0.32)]'
          : 'ring-1 ring-white/12'
      } ${className}`}
      style={{
        width: size,
        height: size,
        background: 'var(--ggj-logo-mark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Math.max(2, Math.round(size * 0.06)),
      }}
    >
      <img
        src="/ggj-mark-ref.png"
        alt="GoGrabJob"
        draggable={false}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          mixBlendMode: 'screen',
          flexShrink: 0,
        }}
      />
    </div>
  );
}
